import {
  Bell, MessageCircle, X, CalendarClock, CheckCircle2, XCircle, Calendar, Send, Pill, CheckCheck,
} from "lucide-react";
import Portal from "./Portal";
import api from "../lib/api";

const iconFor = (kind = "") => {
  if (kind.includes("cancel")) return XCircle;
  if (kind.includes("confirm")) return CheckCircle2;
  if (kind.includes("resched")) return CalendarClock;
  if (kind.includes("referral")) return Send;
  if (kind.includes("prescription") || kind.includes("pharmacy")) return Pill;
  return Calendar;
};
const dotFor = (kind = "") =>
  kind.includes("cancel") ? "bg-[#FF8A8A]" : kind.includes("confirm") ? "bg-[#9BF2C6]" : kind.includes("resched") ? "bg-[#FFD98A]" : "bg-white";

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

const Row = ({ icon: Icon, title, text, time, unread, dot, onClick, testid, badge }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left p-3 rounded-[18px] flex items-start gap-3 cai-glass-hover border ${
      unread ? "bg-[#1E3A5F]/25 border-white/35" : "bg-[#1E3A5F]/12 border-white/15"
    }`}
  >
    <span className="relative w-9 h-9 rounded-full bg-white/20 border border-white/30 grid place-items-center shrink-0 text-white">
      <Icon className="w-4 h-4" strokeWidth={1.7} />
      {unread && <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white/60 ${dot}`} />}
    </span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-white truncate">{title}</span>
        {time && <span className="text-[10px] text-white/70 shrink-0">{time}</span>}
      </span>
      <span className="block text-[12px] text-white/85 mt-0.5 leading-snug">{text}</span>
    </span>
    {badge > 0 && (
      <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#5B8FD0] text-[10.5px] font-bold grid place-items-center">{badge}</span>
    )}
  </button>
);

export const NotificationsList = ({ unreadChat, appNotifs = [], onOpenChat, onRefreshNotifs, onClose = () => {} }) => {
  const empty = unreadChat === 0 && appNotifs.length === 0;
  const markRead = (id) => api.post(`/notifications/${id}/read`).then(() => onRefreshNotifs?.()).catch(() => {});
  return (
    <div className="px-3 pb-3 space-y-2 overflow-y-auto cai-scroll" data-testid="notifications-list">
      {empty && (
        <div className="py-8 text-center text-white" data-testid="notifications-empty">
          <Bell className="w-6 h-6 mx-auto opacity-80" strokeWidth={1.5} />
          <div className="mt-2 text-[13px]">Todo al día</div>
          <div className="text-[11.5px] text-white/75">No tienes notificaciones nuevas.</div>
        </div>
      )}
      {unreadChat > 0 && (
        <Row
          icon={MessageCircle}
          title="Mensajes nuevos"
          text={`Tienes ${unreadChat} mensaje(s) sin leer`}
          unread
          dot="bg-white"
          badge={unreadChat}
          testid="notif-chat"
          onClick={() => { onClose(); onOpenChat(); }}
        />
      )}
      {appNotifs.map((n) => (
        <Row
          key={n.id}
          icon={iconFor(n.kind)}
          title={n.title}
          text={n.message}
          time={timeAgo(n.created_at)}
          unread={!n.read_at}
          dot={dotFor(n.kind)}
          testid={`notif-${n.kind}-${n.id}`}
          onClick={() => markRead(n.id)}
        />
      ))}
    </div>
  );
};

export const MarkAllReadBtn = ({ appNotifs = [], onRefreshNotifs }) =>
  appNotifs.some((n) => !n.read_at) && (
    <button
      type="button"
      onClick={() => api.post(`/notifications/read-all`).then(() => onRefreshNotifs?.()).catch(() => {})}
      data-testid="mark-all-read"
      title="Marcar todo leído"
      className="h-8 px-3 rounded-full bg-white/20 border border-white/30 text-[11px] font-semibold text-white hover:bg-white/30 transition-colors inline-flex items-center gap-1"
    >
      <CheckCheck className="w-3.5 h-3.5" /> Leído
    </button>
  );

export default function NotificationsPanel({ open, onClose, unreadChat, appNotifs = [], onOpenChat, onRefreshNotifs }) {
  if (!open) return null;
  const unreadApp = appNotifs.filter((n) => !n.read_at);

  return (
    <Portal>
      <div
        className="cai-panel fixed z-[55] top-20 right-6 w-[380px] max-h-[78vh] rounded-[28px] overflow-hidden flex flex-col shadow-[0_24px_60px_-18px_rgba(91,143,208,0.7)] cai-fade-in"
        data-testid="notifications-panel"
      >
        <div className="relative z-10 flex flex-col min-h-0">
          <div className="px-5 pt-4 pb-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-white/20 border border-white/30 grid place-items-center">
                <Bell className="w-4 h-4" strokeWidth={1.7} />
              </span>
              <span className="text-[14px] font-medium">Notificaciones</span>
              {unreadApp.length + (unreadChat > 0 ? 1 : 0) > 0 && (
                <span className="px-2 h-5 rounded-full border border-white/40 text-[10.5px] grid place-items-center">
                  {unreadApp.length + (unreadChat > 0 ? 1 : 0)} nuevas
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <MarkAllReadBtn appNotifs={appNotifs} onRefreshNotifs={onRefreshNotifs} />
              <button
                type="button"
                onClick={onClose}
                data-testid="notifications-close"
                className="w-8 h-8 rounded-full bg-white/20 border border-white/30 grid place-items-center hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <NotificationsList unreadChat={unreadChat} appNotifs={appNotifs} onOpenChat={onOpenChat} onRefreshNotifs={onRefreshNotifs} onClose={onClose} />
        </div>
      </div>
    </Portal>
  );
}
